import { useCallback, useEffect, useState } from 'react'
import { getDisplayMode } from '@core/platform'
import { loadSession, signOut, NO_FAMILY, type SessionResult } from '@core/session'
import { supabase } from '@core/supabase'
import { registerSubscription } from '@core/push'
import { InstallGuide } from '@features/install/InstallGuide'
import { SignIn } from '@features/auth/SignIn'
import { Setup } from '@features/onboarding/Setup'
import { installErrorReporter } from '@core/problems'
import { AdminShell } from '@features/shell/AdminShell'
import { MemberShell } from '@features/shell/MemberShell'
import { Button, Screen, Spinner } from '@design/components'

/**
 * Three gates, in order:
 *
 *   installed?  →  signed in?  →  in a family?  →  role decides the shell
 *
 * The install gate is first and is not skippable: without the home-screen
 * install there is no push, and without push the parent never learns their
 * invoice was handled — which is the whole reason they would use this instead
 * of WhatsApp.
 *
 * The role decides which shell renders, but that is cosmetic. What a session
 * may actually read is decided by row-level security in Postgres, which
 * applies to this same token no matter what is on screen.
 */
export function App() {
  const [mode, setMode] = useState(getDisplayMode)
  const [session, setSession] = useState<SessionResult | undefined>(undefined)
  const [loadFailed, setLoadFailed] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setLoadFailed(false)
      setSession(await loadSession())
    } catch (err) {
      // Deliberately NOT setSession(null). Null means "signed out", and
      // treating a failed read as a sign-out threw freshly authenticated
      // people back to the code screen with no explanation — and their
      // one-time code already spent, so the retry failed too. A failure to
      // read is a failure to read, and it says so.
      console.error('Could not load session', err)
      setLoadFailed(true)
    }
  }, [])

  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)')
    const onChange = () => setMode(getDisplayMode())
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    void refresh()
    const { data } = supabase.auth.onAuthStateChange(() => void refresh())
    return () => data.subscription.unsubscribe()
  }, [refresh])

  // Push subscriptions expire without warning, and a dead subscription looks
  // exactly like a working one until a notification silently fails to arrive.
  // Re-registering on every launch is what quietly repairs that.
  useEffect(() => {
    if (session && session !== NO_FAMILY) void registerSubscription(session.userId)
  }, [session])

  // Installed once a session exists, so a crash can be attributed to someone.
  // Before sign-in there is nowhere to write it and nobody to tell.
  useEffect(() => {
    if (session && session !== NO_FAMILY) installErrorReporter(session.userId)
  }, [session])

  const handleSignOut = async () => {
    await signOut()
    await refresh()
  }

  if (mode === 'browser') return <InstallGuide />

  if (loadFailed) {
    return (
      <Screen className="flex flex-col justify-center max-w-md mx-auto w-full pad-safe-top text-center">
        <h1 className="text-xl font-semibold mb-2">Even niet gelukt</h1>
        <p className="text-muted mb-6">
          Je bent wel aangemeld, maar we konden je gegevens niet ophalen. Probeer het opnieuw.
        </p>
        <Button size="large" onClick={() => void refresh()}>
          Opnieuw proberen
        </Button>
        <div className="mt-3">
          <Button tone="quiet" onClick={handleSignOut}>
            Afmelden
          </Button>
        </div>
      </Screen>
    )
  }

  if (session === undefined) return <Spinner label="Even geduld…" />
  if (session === null) return <SignIn />
  if (session === NO_FAMILY) return <Setup onDone={() => void refresh()} />

  return session.role === 'admin' ? (
    <AdminShell session={session} onSignOut={handleSignOut} />
  ) : (
    <MemberShell session={session} onSignOut={handleSignOut} />
  )
}
